import React from "react";
import { AuthContext, AuthContextType, LoginResult } from "../../Types/AuthContext";
import AuthMeResponse from "../../Repositories/AuthResponses/AuthMeResponse";
import LoginRepository from "../../Repositories/AuthRepository";
import AuthLoginResponse from "../../Repositories/AuthResponses/AuthLoginResponse";

interface AuthProviderProps {
  children?: React.ReactNode;
}

interface AuthProviderState {
  token: string;
  fullName: string;
  userId: string;
  permissions: string[];
  loading: boolean;
  locationId: string;
  error?: string | null;
}

export default class AuthProvider extends React.Component<AuthProviderProps, AuthProviderState> {
  constructor(props: any) {
    super(props);

    this.login = this.login.bind(this);
    this.logout = this.logout.bind(this);
    this.refreshMe = this.refreshMe.bind(this);

    this.state = {
      token: "",
      fullName: "",
      userId: "",
      permissions: [],
      loading: true,
      locationId: "",
      error: null,
    };
  }

  public async componentDidMount() {
    await this.refreshMe();
  }

  private async refreshMe() {
    try {
      const res = await LoginRepository.me();
      if (res.success && res.result) {
        const data = {
          username: res.result.username,
          fullName: res.result.fullName,
          userId: res.result.userId,
          permissions: res.result.permissions,
          locationId: res.result.locationId
        } as AuthMeResponse;

        this.setState({
          token: "",
          fullName: data.fullName ?? "",
          userId: data.userId,
          permissions: Array.isArray(data.permissions) ? data.permissions : [],
          locationId: data.locationId,
          loading: false,
          error: null,
        });
      } else {
        this.setState({
          token: "",
          fullName: "",
          userId: "",
          permissions: [],
          locationId: "",
          loading: false,
          error: null,
        });
      }
    } catch {
      this.setState({
        token: "",
        fullName: "",
        userId: "",
        permissions: [],
        locationId: "",
        loading: false,
        error: null,
      });
    }
  }

  // returns ok on success so caller can redirect; error carries the reason
  public async login(username: string, password: string): Promise<LoginResult> {
    const res = await LoginRepository.login(username, password, true);
    if (!res.success || !res.result) {
      const error = res.errorMessage ?? "Invalid username or password";
      this.setState({ error });
      return { ok: false, error };
    }

    const data /* as LoginResponse */ = {
      success: res.result.success,
      errorMessage: res.result.errorMessage ?? null,
      username: res.result.username,
      fullName: res.result.fullName,
      userId: res.result.userId,
      locationId: res.result.locationId,
      permissions: res.result.permissions
    } as AuthLoginResponse;

    if (!data.success) {
      const error = data.errorMessage ?? "Login failed";
      this.setState({ error });
      return { ok: false, error };
    }

    this.setState({
      token: "",
      fullName: data.fullName ?? data.username ?? "",
      userId: data.userId ?? "",
      permissions: Array.isArray(data.permissions) ? data.permissions : [],
      locationId: data.locationId ?? "",
      error: null,
    });

    return { ok: true };
  }

  public async logout(): Promise<void> {
    try { await LoginRepository.logout(); } catch { /* ignore */ }
    this.setState({
      token: "",
      fullName: "",
      permissions: [],
      locationId: "",
      // keep loading false
    });
  }

  public render() {
    const contextValue: AuthContextType = {
      token: this.state.token,
      fullName: this.state.fullName,
      userId: this.state.userId,
      permissions: this.state.permissions,
      locationId: this.state.locationId,
      login: this.login,
      logout: this.logout,
    };

    if (this.state.loading) return null;

    return (
      <AuthContext.Provider value={contextValue}>
        {this.props.children}
      </AuthContext.Provider>
    );
  }
}